import React, { useState, useEffect } from 'react';
import { TextField, Box, Typography, CircularProgress, Alert, Autocomplete } from '@mui/material';
import Grid from '@mui/material/Grid';

import { LocationOn } from '@mui/icons-material';
import { buildApiUrl } from '@config/api';

interface AddressData {
  street: string;
  streetNumber: string;
  city: string;
  county: string;
  postalCode: string;
  country: string;
  sector?: string; // Pentru sectoarele Bucurestiului
}

interface SimpleAddressFormProps {
  value: AddressData;
  onChange: (address: AddressData) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
}

interface RomanianAddress {
  id: number;
  judet: string;
  oras: string;
  strada?: string;
  numar?: string;
  cod_postal: string;
  tip_artera?: string;
  denumire_artera?: string;
}

const SimpleAddressForm: React.FC<SimpleAddressFormProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  required = false
}) => {
  const [postalCode, setPostalCode] = useState(value.postalCode || '');
  const [availableCounties, setAvailableCounties] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [availableStreets, setAvailableStreets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState('');

  // Actualizeaza starea interna cand se schimba valoarea din exterior
  useEffect(() => {
    setPostalCode(value.postalCode || '');
  }, [value.postalCode]);

  // Verifica daca orasul este Bucuresti pentru a afisa sector in loc de judet
  const isBucharest = value.city?.toLowerCase().includes('sector') && 
                      (value.city?.toLowerCase().includes('bucuresti') || 
                       value.city?.toLowerCase().includes('bucuresti') ||
                       value.city?.toLowerCase().includes('bucureşti') ||
                       value.county?.toLowerCase().includes('bucuresti') ||
                       value.county?.toLowerCase().includes('bucuresti') ||
                       value.county?.toLowerCase().includes('bucureşti') ||
                       value.county?.toLowerCase().includes('municipiul bucuresti') ||
                       value.county?.toLowerCase().includes('municipiul bucuresti') ||
                       value.county?.toLowerCase().includes('municipiul bucureşti'));

  // Extrage sectorul din orasul Bucuresti
  const extractSector = (city: string): string => {
    if (!city) return '';
    const sectorMatch = city.match(/sector\s*(\d+)/i);
    return sectorMatch ? `Sector ${sectorMatch[1]}` : '';
  };

  // Debug logging
  console.log('🏙️ SimpleAddressForm Debug:', {
    city: value.city,
    county: value.county,
    cityLower: value.city?.toLowerCase(),
    countyLower: value.county?.toLowerCase(),
    hasSector: value.city?.toLowerCase().includes('sector'),
    hasBucuresti: value.city?.toLowerCase().includes('bucuresti') || value.city?.toLowerCase().includes('bucuresti') || value.city?.toLowerCase().includes('bucureşti') || value.county?.toLowerCase().includes('bucuresti') || value.county?.toLowerCase().includes('bucuresti') || value.county?.toLowerCase().includes('bucureşti') || value.county?.toLowerCase().includes('municipiul bucuresti') || value.county?.toLowerCase().includes('municipiul bucuresti') || value.county?.toLowerCase().includes('municipiul bucureşti'),
    isBucharest,
    extractedSector: extractSector(value.city || '')
  });

  // Incarca judetele disponibile la montarea componentei
  useEffect(() => {
    loadCounties();
  }, []);

  // Incarca judetele din baza de date
  const loadCounties = async () => {
    try {
      const response = await fetch(buildApiUrl('/companies/judete'));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableCounties(data.data);
        }
      }
    } catch (error) {
      console.error('Eroare la incarcarea judetelor:', error);
    }
  };

  // Cand se schimba codul postal, cauta adresele corespunzatoare
  const handlePostalCodeChange = async (newPostalCode: string) => {
    setPostalCode(newPostalCode);
    setPostalCodeError('');
    
    if (newPostalCode.length >= 6) {
      setLoading(true);
      try {
        const response = await fetch(buildApiUrl(`/addresses?cod_postal=${newPostalCode}&limit=10`));
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.length > 0) {
            const address = data.data[0];
            
            // Pre-completeaza adresa din baza de date
            onChange({
              ...value,
              postalCode: newPostalCode,
              county: address.judet || '',
              city: address.oras || '',
              street: address.denumire_artera || '',
              streetNumber: address.numar || '',
              country: 'Romania'
            });

            // Incarca orasele pentru judetul gasit
            if (address.judet) {
              loadCitiesByCounty(address.judet);
            }
          } else {
            setPostalCodeError('Codul postal nu a fost gasit in baza de date');
          }
        }
      } catch (error) {
        console.error('Eroare la cautarea codului postal:', error);
        setPostalCodeError('Eroare la cautarea codului postal');
      } finally {
        setLoading(false);
      }
    }
  };

  // Incarca orasele pentru un judet
  const loadCitiesByCounty = async (county: string) => {
    try {
      const response = await fetch(buildApiUrl(`/addresses/orase?judet=${encodeURIComponent(county)}`));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableCities(data.data);
        }
      }
    } catch (error) {
      console.error('Eroare la incarcarea oraselor:', error);
    }
  };

  // Incarca strazile pentru un oras
  const loadStreetsByCity = async (city: string) => {
    try {
      const response = await fetch(buildApiUrl(`/addresses/strazi?oras=${encodeURIComponent(city)}&limit=50`));
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableStreets(data.data);
        }
      }
    } catch (error) {
      console.error('Eroare la incarcarea strazilor:', error);
    }
  };

  // Handler pentru schimbarea judetului
  const handleCountyChange = (newCounty: string) => {
    onChange({ ...value, county: newCounty, city: '', street: '', streetNumber: '' });
    setAvailableCities([]);
    setAvailableStreets([]);
    if (newCounty) {
      loadCitiesByCounty(newCounty);
    }
  };

  // Handler pentru schimbarea orasului
  const handleCityChange = (newCity: string) => {
    onChange({ ...value, city: newCity, street: '', streetNumber: '' });
    setAvailableStreets([]);
    if (newCity) {
      loadStreetsByCity(newCity);
    }
  };

  // Handler pentru schimbarea strazii
  const handleStreetChange = (newStreet: string) => {
    onChange({ ...value, street: newStreet });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
        Adresa clinicii
      </Typography>
      
      <Grid container spacing={2}>
        {/* Cod Postal - Primul camp */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Cod Postal *"
            value={postalCode}
            onChange={(e) => handlePostalCodeChange(e.target.value)}
            error={!!postalCodeError}
            helperText={postalCodeError || 'Introduceti codul postal pentru a pre-completa adresa'}
            required
            disabled={disabled}
            InputProps={{
              endAdornment: loading && <CircularProgress size={20} />
            }}
          />
        </Grid>

        {/* Judet sau Sector (pentru Bucuresti) */}
        <Grid size={{ xs: 12, sm: 6 }}>
          {isBucharest ? (
            <TextField
              fullWidth
              label="Sector *"
              value={extractSector(value.city)}
              disabled={true}
              helperText="Sectorul este extras automat din orasul Bucuresti"
            />
          ) : (
            <Autocomplete
              options={availableCounties}
              value={value.county}
              onChange={(_, newValue) => handleCountyChange(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Judet *"
                  required
                  disabled={disabled || !postalCode}
                  helperText={!postalCode ? 'Introduceti mai intai codul postal' : ''}
                />
              )}
              disabled={disabled || !postalCode}
            />
          )}
        </Grid>

        {/* Oras */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            options={availableCities}
            value={value.city}
            onChange={(_, newValue) => handleCityChange(newValue || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Oras *"
                required
                disabled={disabled || !value.county}
                helperText={!value.county ? 'Selectati mai intai judetul' : ''}
              />
            )}
            disabled={disabled || !value.county}
          />
        </Grid>

        {/* Strada */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            options={availableStreets}
            value={value.street}
            onChange={(_, newValue) => handleStreetChange(newValue || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Strada *"
                required
                disabled={disabled || !value.city}
                helperText={!value.city ? 'Selectati mai intai orasul' : ''}
              />
            )}
            disabled={disabled || !value.city}
          />
        </Grid>

        {/* Numarul strazii */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Numarul strazii"
            value={value.streetNumber || ''}
            onChange={(e) => onChange({ ...value, streetNumber: e.target.value })}
            disabled={disabled}
            placeholder="ex: 15A, 23B"
          />
        </Grid>

        {/* Tara */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Tara"
            value={value.country}
            onChange={(e) => onChange({ ...value, country: e.target.value })}
            disabled={disabled}
            defaultValue="Romania"
          />
        </Grid>
      </Grid>

      {helperText && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {helperText}
        </Alert>
      )}
    </Box>
  );
};

export default SimpleAddressForm; 
